import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  inject,
} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {TranslateModule} from "@ngx-translate/core";
import {Subscription, catchError, from, map, mergeMap, of} from "rxjs";
import {DiscussionTextPart} from "../../interfaces/discussion.interface";
import {PublicWorkService} from "src/app/modules/works/services/public-work.service";
import {PublicWorkPreviewComponent} from "src/app/modules/works/components/public-work-preview/public-work-preview.component";
import type {PublicWorkResponse} from "src/app/modules/works/interfaces/public-work.interface";
import {isPublicWorkLink} from "src/app/modules/works/utils/public-work-link.util";
import {AuthService} from "src/app/modules/auth/services/auth.service";

@Component({
  selector: "app-discussion-text",
  templateUrl: "./discussion-text.component.html",
  imports: [PublicWorkPreviewComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscussionTextComponent implements OnChanges {
  @Input() text = "";
  @Output() openWork = new EventEmitter<PublicWorkResponse>();
  parts: DiscussionTextPart[] = [];
  opening?: DiscussionTextPart;
  error = "";
  private readonly service = inject(PublicWorkService);
  private readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private previews?: Subscription;
  private openingRequest?: Subscription;

  ngOnChanges(): void {
    this.previews?.unsubscribe();
    this.openingRequest?.unsubscribe();
    this.opening = undefined;
    this.error = "";
    this.parts = [];
    let offset = 0;
    for (const match of this.text.matchAll(/https?:\/\/[^\s<>]+/gi)) {
      const start = match.index ?? 0;
      if (start > offset)
        this.parts.push({text: this.text.slice(offset, start)});
      const href = match[0].replace(/[.,!?;:)]+$/, "");
      try {
        const url = new URL(href);
        const label = url.hostname + (url.pathname === "/" ? "" : url.pathname);
        this.parts.push({
          href: url.href,
          text: label.length > 40 ? label.slice(0, 37) + "…" : label,
          workUrl: isPublicWorkLink(url) ? url : undefined,
        });
      } catch {
        this.parts.push({text: href});
      }
      offset = start + href.length;
    }
    if (offset < this.text.length)
      this.parts.push({text: this.text.slice(offset)});
    this.loadPreviews();
  }

  open(part: DiscussionTextPart): void {
    if (!part.workUrl || this.opening) return;
    this.opening = part;
    this.error = "";
    this.openingRequest = this.read(part.workUrl)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.opening = undefined;
        if (data) this.openWork.emit(data);
        else this.error = "DISCUSSION.WORK_UNAVAILABLE";
        this.cdr.markForCheck();
      });
  }

  private loadPreviews(): void {
    const links = new Map(
      this.parts
        .filter((part) => part.workUrl)
        .map((part) => [part.href, part.workUrl!])
    );
    this.previews = from(links.values())
      .pipe(
        mergeMap(
          (url) => this.read(url, true).pipe(map((data) => ({url, data}))),
          2
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({url, data}) => {
        for (const part of this.parts)
          if (part.href === url.href) part.preview = data;
        this.cdr.markForCheck();
      });
  }

  private read(url: URL, preview = false) {
    const [, , type, id] = url.pathname.split("/");
    const user = this.auth.user;
    return this.service
      .read(
        type,
        id,
        url.searchParams.get("lang") ?? user?.language ?? "en",
        url.searchParams.get("country") ?? user?.country ?? "US",
        url.searchParams.get("s") ?? undefined,
        preview
      )
      .pipe(catchError(() => of(undefined)));
  }
}
